import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2 } from "lucide-react";

const CATS = ["Beginner", "Forex", "Technical Analysis", "Fundamental Analysis", "Risk Management", "Trading Psychology", "Platforms", "Trading Strategies"];
const BLANK = { title: "", slug: "", level: CATS[0], category: CATS[0], excerpt: "", body: "", reading_time: 5, order: 99, status: "published", seo_title: "", seo_description: "" };

export default function EducationAdmin() {
  const qc = useQueryClient();
  const { data: lessons = [] } = useQuery({ queryKey: ["admin-education"], queryFn: async () => (await api.get("/admin/education")).data });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [editId, setEditId] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = useMutation({
    mutationFn: async () => {
      const payload = { ...form, category: form.level, reading_time: Number(form.reading_time) || 5, order: Number(form.order) || 99 };
      if (editId) return api.put(`/admin/education/${editId}`, payload);
      return api.post("/admin/education", payload);
    },
    onSuccess: () => { toast.success("Saved"); qc.invalidateQueries({ queryKey: ["admin-education"] }); setOpen(false); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const del = useMutation({ mutationFn: async (id) => api.delete(`/admin/education/${id}`), onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["admin-education"] }); } });

  const openNew = () => { setForm(BLANK); setEditId(null); setOpen(true); };
  const openEdit = (l) => { setForm({ ...BLANK, ...l }); setEditId(l.id); setOpen(true); };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-2xl font-bold text-ink">Education</h1><p className="mt-1 text-sm text-subtle">Manage educational lessons.</p></div>
        <Button onClick={openNew} className="bg-finblue hover:bg-finblueDark" data-testid="education-add"><Plus className="h-4 w-4" /> New Lesson</Button>
      </div>

      <div className="mt-6 overflow-hidden rounded-xl border border-hair bg-white shadow-sm">
        <Table>
          <TableHeader><TableRow className="bg-canvas"><TableHead>Title</TableHead><TableHead>Level</TableHead><TableHead>Order</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {lessons.map((l) => (
              <TableRow key={l.id}>
                <TableCell className="font-medium text-ink">{l.title}</TableCell>
                <TableCell className="text-sm text-subtle">{l.level}</TableCell>
                <TableCell className="text-sm">{l.order}</TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(l)}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => del.mutate(l.id)}><Trash2 className="h-4 w-4 text-danger" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit Lesson" : "New Lesson"}</DialogTitle></DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <F label="Title" full><Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="education-form-title" /></F>
            <F label="Level"><Select value={form.level} onValueChange={(v) => set("level", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></F>
            <F label="Order"><Input type="number" value={form.order} onChange={(e) => set("order", e.target.value)} /></F>
            <F label="Excerpt" full><Textarea rows={2} value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} /></F>
            <F label="Body (HTML)" full><Textarea rows={8} value={form.body} onChange={(e) => set("body", e.target.value)} /></F>
            <div className="sm:col-span-2 rounded-lg border border-dashed border-hair p-3">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-finblue">العربية / Arabic (optional)</p>
              <div className="grid gap-4">
                <F label="Title (AR)" full><Input dir="rtl" value={form.title_ar} onChange={(e) => set("title_ar", e.target.value)} /></F>
                <F label="Excerpt (AR)" full><Textarea dir="rtl" rows={2} value={form.excerpt_ar} onChange={(e) => set("excerpt_ar", e.target.value)} /></F>
                <F label="Body AR (HTML)" full><Textarea dir="rtl" rows={6} value={form.body_ar} onChange={(e) => set("body_ar", e.target.value)} /></F>
              </div>
            </div>
          </div>
          <DialogFooter><Button onClick={() => save.mutate()} disabled={save.isPending} className="bg-finblue hover:bg-finblueDark" data-testid="education-form-save">{save.isPending ? "Saving..." : "Save Lesson"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, full, children }) {
  return <div className={`space-y-1.5 ${full ? "sm:col-span-2" : ""}`}><Label className="text-sm">{label}</Label>{children}</div>;
}
